/**
 * Typed i18n. English is the source of truth; the Spanish dictionary is
 * typed `typeof` its English counterpart, so a missing or extra key is a
 * compile error, not a runtime surprise.
 *
 * Strings needing interpolation are functions, so each language controls
 * its own grammar.
 */
/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, type ReactNode } from 'react';
import type { Locale } from '../lib/types';
import * as en from './en';
import * as es from './es';

export type Dict = typeof en;

const dicts: Record<Locale, Dict> = { en, es };

const LocaleCtx = createContext<Locale>('en');

export function I18nProvider({ locale, children }: { locale: Locale; children: ReactNode }) {
  return <LocaleCtx.Provider value={locale}>{children}</LocaleCtx.Provider>;
}

export function useLocale(): Locale {
  return useContext(LocaleCtx);
}

export function useT(): Dict {
  return dicts[useContext(LocaleCtx)];
}

/** Detect the best starting locale from the browser. */
export function detectLocale(): Locale {
  return navigator.language?.toLowerCase().startsWith('es') ? 'es' : 'en';
}
